import { useEffect, useRef, type RefObject } from "react";

/** A value whose change re-runs the focus. Primitives only, compared by value. */
export type FocusKey = string | number | boolean | null | undefined;

/**
 * THE ONE WAY A SCREEN MOVES FOCUS TO ITS HEADING (or another landmark of the
 * new state) when that state appears.
 *
 * Returns a ref for the target (a heading with `tabIndex={-1}`, usually). The
 * focus is an EFFECT: it runs after the commit that renders the target, on
 * mount, and again whenever a value in `on` changes — the phases that replace
 * what was on screen. `when` false skips that run without changing when the
 * next one happens, for states that must leave focus where the visitor is
 * (a busy button, a form still in use).
 *
 * `on` is compared by value (JSON), so callers can pass several primitives
 * without building a memoised array. `npm run quality:contracts`
 * (scripts/lib/one-way-contracts.js) refuses a hand-written
 * `useEffect(() => headingRef.current?.focus())` anywhere else.
 */
export function useFocusOnMount<T extends HTMLElement = HTMLHeadingElement>({
  on = [],
  when = true,
}: { on?: readonly FocusKey[]; when?: boolean } = {}): RefObject<T | null> {
  const ref = useRef<T>(null);
  const key = JSON.stringify(on);
  useEffect(() => {
    if (!when) return;
    const target = ref.current;
    if (!target) return;
    target.focus();
    // A target that could not take focus because its page is not displayed
    // yet is kept for the reveal (`focusDeferred`); any later focus that
    // lands replaces it.
    deferred = document.activeElement === target ? null : target;
  }, [key, when]);
  return ref;
}

let deferred: HTMLElement | null = null;

/**
 * THE PAGE WAS JUST REVEALED: give the last arrival focus that could not land
 * its focus now. Book's Spanish boot (rc.12 (b)) renders the tree while #root
 * is not displayed, and `.focus()` on a hidden element does nothing; without
 * this, an arrival that focuses its heading (the appointment page, a failed
 * read) would leave a Spanish reader on <body>. Only when focus is still
 * nowhere: something the reader focused meanwhile stands.
 */
export function focusDeferred(): void {
  const target = deferred;
  deferred = null;
  if (!target?.isConnected) return;
  const active = document.activeElement;
  if (active === null || active === document.body) target.focus();
}
